import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Bell, BookOpen, CheckCircle2, AlertCircle, UserPlus, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ICON_MAP = {
  assignment: BookOpen,
  success: CheckCircle2,
  info: AlertCircle,
  welcome: UserPlus,
};

const COLOR_MAP = {
  assignment: 'text-blue-500 bg-blue-50',
  success: 'text-green-500 bg-green-50',
  info: 'text-amber-500 bg-amber-50',
  welcome: 'text-[#1B2A4A] bg-[#1B2A4A]/10',
};

const fadeIn = { initial: { opacity: 0, y: -10, scale: 0.95 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: -10, scale: 0.95 } };

export default function NotificationBell() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const apiRef = useRef(api);

  useEffect(() => { apiRef.current = api; }, [api]);

  const loadNotifs = useCallback(async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        apiRef.current('get', '/notifications'),
        apiRef.current('get', '/notifications/unread-count')
      ]);
      setNotifications(notifsRes.data);
      setUnread(countRes.data.count);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load notifications:', err.message);
    }
  }, []);

  useEffect(() => {
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, [loadNotifs]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    try {
      await apiRef.current('post', '/notifications/mark-read', { ids: [] });
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Mark read failed:', err.message);
    }
  };

  const handleClick = (notif) => {
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
    if (!notif.read) {
      apiRef.current('post', '/notifications/mark-read', { ids: [notif.id] }).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="sm" onClick={() => setOpen(!open)}
        className="relative rounded-xl text-stone-600 hover:bg-stone-100 p-2" data-testid="notification-bell">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px] px-1" data-testid="unread-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div {...fadeIn} transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl border border-stone-200 shadow-xl z-50 overflow-hidden"
            data-testid="notification-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Notifications</h3>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button onClick={markAllRead} className="text-xs text-[#1B2A4A] hover:underline font-medium" data-testid="mark-all-read">Mark all read</button>
                )}
                <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-600"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto" data-testid="notification-list">
              {notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                  <p className="text-sm text-stone-500">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const Icon = ICON_MAP[notif.type] || AlertCircle;
                  const colorClass = COLOR_MAP[notif.type] || 'text-stone-500 bg-stone-50';
                  return (
                    <div key={notif.id} onClick={() => handleClick(notif)}
                      className={`flex items-start gap-3 px-4 py-3 border-b border-stone-50 cursor-pointer transition-colors hover:bg-stone-50 ${!notif.read ? 'bg-blue-50/30' : ''}`}
                      data-testid={`notification-item-${notif.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}><Icon className="w-4 h-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1">
                          <p className={`text-sm ${!notif.read ? 'font-semibold text-stone-900' : 'font-medium text-stone-700'}`}>{notif.title}</p>
                          {!notif.read && <div className="w-2 h-2 bg-[#1B2A4A] rounded-full shrink-0 mt-1.5" />}
                        </div>
                        <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-stone-400 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
